import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Alert,
  Chip,
  Checkbox,
  Collapse,
  IconButton,
  Divider,
  CircularProgress,
  alpha,
  Stack
} from '@mui/material';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { nanoid } from 'nanoid';
import { getDisplayColumnPrefix } from '../../../utils/periodUtils';
import { normalizeCategoryType } from '../../../utils/tagValueUtils';

const STEPS = ['Select Library', 'Map Columns', 'Configure & Import'];

const ACTION_OPTIONS = [
  { value: 'skip_row', label: 'Skip Row', description: 'Silently skip the row' },
  { value: 'fail_row', label: 'Fail Row', description: 'Checked against the table when the file is approved: fails the file' },
];

const OPERATOR_CONFIG = {
  is_empty: { types: ['Text', 'Number', 'Date', 'Yes/No'] },
  is_not_empty: { types: ['Text', 'Number', 'Date', 'Yes/No'] },
  equals: { types: ['Text', 'Number', 'Date', 'Yes/No'], supportsColumnRhs: true },
  not_equals: { types: ['Text', 'Number', 'Date', 'Yes/No'], supportsColumnRhs: true },
  contains: { types: ['Text'] },
  not_contains: { types: ['Text'] },
  greater_than: { types: ['Number', 'Date'], supportsColumnRhs: true },
  less_than: { types: ['Number', 'Date'], supportsColumnRhs: true },
  greater_than_or_equal: { types: ['Number', 'Date'], supportsColumnRhs: true },
  less_than_or_equal: { types: ['Number', 'Date'], supportsColumnRhs: true },
  between: { types: ['Number', 'Date'], supportsColumnRhs: true },
  is_date: { types: ['Text', 'Number', 'Date', 'Yes/No'] },
  is_number: { types: ['Text', 'Number', 'Date', 'Yes/No'] },
  is_text: { types: ['Text', 'Number', 'Date', 'Yes/No'] },
  is_boolean: { types: ['Text', 'Number', 'Date', 'Yes/No'] },
  length_equals: { types: ['Text'] },
  length_greater_than: { types: ['Text'] },
  length_greater_than_or_equal: { types: ['Text'] },
  length_less_than: { types: ['Text'] },
  length_less_than_or_equal: { types: ['Text'] },
  starts_with: { types: ['Text'] },
  ends_with: { types: ['Text'] },
  matches_regex: { types: ['Text'] },
};

const TEXT_TRANSFORMS = new Set([
  'uppercase',
  'lowercase',
  'trim',
  'remove_spaces',
  'strip_leading_zeros',
  'keep_digits',
  'remove_characters',
  'left',
  'right',
  'mid',
  'replace',
  'pad_left',
  'pad_right',
  'drop_prefix',
  'drop_suffix',
  'cast_number_string',
]);

/**
 * Build the list of available table columns, matching the logic in IngestionRuleRow.js
 */
function getTableColumns(tableSchema) {
  const columns = [];

  if (tableSchema.identifier?.name) {
    columns.push(tableSchema.identifier.name);
  }
  if (tableSchema.detailColumns) {
    tableSchema.detailColumns.forEach(col => columns.push(col.name));
  }
  if (tableSchema.attributeColumns) {
    tableSchema.attributeColumns.forEach(col => columns.push(col.name));
  }
  if (tableSchema.tableAttributeColumns) {
    tableSchema.tableAttributeColumns.forEach(col => columns.push(col.name));
  }
  // Only a time-series table has metrics, each under its period prefix, and the
  // period is a column of its own.
  if (tableSchema.metricColumns && tableSchema.metricColumns.schema) {
    const periodPrefix = getDisplayColumnPrefix(tableSchema);
    Object.entries(tableSchema.metricColumns.schema).forEach(([key, value]) => {
      if (value.type) {
        columns.push(`${periodPrefix}.${key}`);
      } else {
        Object.keys(value).forEach((subKey) => {
          columns.push(`${periodPrefix}.${key}.${subKey}`);
        });
      }
    });
    columns.push(periodPrefix);
  }
  return columns;
}

function getTableColumnTypeMap(tableSchema) {
  const typeMap = {};

  if (tableSchema?.identifier?.name) {
    typeMap[tableSchema.identifier.name] = tableSchema.identifier.type || 'Text';
  }
  (tableSchema?.detailColumns || []).forEach((column) => {
    typeMap[column.name] = column.type || 'Text';
  });
  (tableSchema?.attributeColumns || []).forEach((column) => {
    typeMap[column.name] = normalizeCategoryType(column.type);
  });
  (tableSchema?.tableAttributeColumns || []).forEach((column) => {
    typeMap[column.name] = normalizeCategoryType(column.type);
  });

  // A time-series table's metrics, under the period prefix, and its period.
  if (tableSchema?.metricColumns?.schema) {
    const periodPrefix = getDisplayColumnPrefix(tableSchema);
    Object.entries(tableSchema.metricColumns.schema).forEach(([key, value]) => {
      if (value.type) {
        typeMap[`${periodPrefix}.${key}`] = value.type;
      } else {
        Object.entries(value).forEach(([subKey, subValue]) => {
          typeMap[`${periodPrefix}.${key}.${subKey}`] = subValue.type || 'Text';
        });
      }
    });
    typeMap[periodPrefix] = 'Date';
  }

  return typeMap;
}

/**
 * Dice coefficient (bigram similarity) between two strings.
 * Returns a value between 0 (no similarity) and 1 (identical).
 */
function diceSimilarity(a, b) {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const bigrams = (s) => {
    const set = new Map();
    for (let i = 0; i < s.length - 1; i++) {
      const bg = s.substring(i, i + 2);
      set.set(bg, (set.get(bg) || 0) + 1);
    }
    return set;
  };
  const bigramsA = bigrams(a);
  const bigramsB = bigrams(b);
  let intersection = 0;
  for (const [bg, count] of bigramsA) {
    if (bigramsB.has(bg)) intersection += Math.min(count, bigramsB.get(bg));
  }
  return (2 * intersection) / (a.length - 1 + b.length - 1);
}

/**
 * Normalize a column name for comparison: lowercase, strip period. prefix,
 * replace underscores/hyphens/special chars with spaces, collapse whitespace.
 */
function normalizeForMatch(name) {
  return name
    .toLowerCase()
    .replace(/^period\./, '')
    .replace(/[_\-/(),.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const FUZZY_THRESHOLD = 0.45;

/**
 * Auto-match canonical columns to table columns using layered matching:
 * 1. Exact match after normalization
 * 2. One name contains the other
 * 3. Best Dice coefficient similarity above threshold
 */
function autoMatchColumns(canonicalColumns, tableColumns) {
  const mapping = {};
  const matched = new Set(); // track used table columns to avoid duplicates

  const tableColsNorm = tableColumns.map(c => normalizeForMatch(c));

  canonicalColumns.forEach(({ key }) => {
    const canonNorm = normalizeForMatch(key);

    // 1. Exact normalized match
    const exactIdx = tableColsNorm.findIndex((tc, i) => !matched.has(i) && tc === canonNorm);
    if (exactIdx !== -1) {
      mapping[key] = tableColumns[exactIdx];
      matched.add(exactIdx);
      return;
    }

    // 2. Containment match (one contains the other)
    const containIdx = tableColsNorm.findIndex((tc, i) =>
      !matched.has(i) && (tc.includes(canonNorm) || canonNorm.includes(tc))
    );
    if (containIdx !== -1) {
      mapping[key] = tableColumns[containIdx];
      matched.add(containIdx);
      return;
    }

    // 3. Fuzzy match via Dice coefficient - pick the best above threshold
    let bestScore = 0;
    let bestIdx = -1;
    tableColsNorm.forEach((tc, i) => {
      if (matched.has(i)) return;
      const score = diceSimilarity(canonNorm, tc);
      if (score > bestScore) {
        bestScore = score;
        bestIdx = i;
      }
    });
    if (bestIdx !== -1 && bestScore >= FUZZY_THRESHOLD) {
      mapping[key] = tableColumns[bestIdx];
      matched.add(bestIdx);
    }
  });
  return mapping;
}

/**
 * Collect all unique canonical column keys referenced in a library's rules.
 */
function getReferencedColumns(rules) {
  const refs = new Set();

  const collectConditionRefs = (condition) => {
    if (!condition) return;
    if (condition.column) refs.add(condition.column);
    if ((condition.compare_source === 'column' || (!condition.compare_source && condition.compare_column)) && condition.compare_column) {
      refs.add(condition.compare_column);
    }
    if ((condition.compare_source2 === 'column' || (!condition.compare_source2 && condition.compare_column2)) && condition.compare_column2) {
      refs.add(condition.compare_column2);
    }
  };

  for (const rule of rules) {
    for (const cond of (rule.conditions || [])) {
      collectConditionRefs(cond);
    }
    const action = rule.action || {};
    const target = rule.level === 'cell' ? rule.target_column : action.column;
    if (target) refs.add(target);
    if (action.source_column) refs.add(action.source_column);
    for (const col of (action.source_columns || [])) {
      if (col) refs.add(col);
    }
    if (action.formula_config?.source_column) refs.add(action.formula_config.source_column);
    if (action.formula_config?.operand_column) refs.add(action.formula_config.operand_column);
    collectConditionRefs(action.formula_config?.condition);
  }
  return refs;
}

/**
 * Resolve a single rule's canonical column names using the provided mapping.
 * Returns a new rule object with all canonical references replaced.
 */
function resolveRule(rule, columnMapping) {
  const resolve = (canonicalName) => columnMapping[canonicalName] || canonicalName;
  // Only the references a condition actually carries are rewritten: a library
  // condition omits the optional comparison columns, and writing them back as
  // `undefined` would make the whole settings document unsaveable (Firestore
  // rejects undefined anywhere in an update).
  const resolveCondition = (condition) => {
    const next = { ...condition, id: nanoid() };
    if (condition.column) next.column = resolve(condition.column);
    if (condition.compare_column) next.compare_column = resolve(condition.compare_column);
    if (condition.compare_column2) next.compare_column2 = resolve(condition.compare_column2);
    return next;
  };

  const resolved = JSON.parse(JSON.stringify(rule)); // deep clone

  // Resolve conditions
  if (resolved.conditions) {
    resolved.conditions = resolved.conditions.map(resolveCondition);
  }

  // Resolve target_column
  if (resolved.level !== 'cell') {
    delete resolved.target_column;
  } else if (resolved.target_column) {
    resolved.target_column = resolve(resolved.target_column);
  }

  // Resolve action references
  if (resolved.action) {
    if (resolved.level === 'cell') {
      delete resolved.action.column;
    } else if (resolved.action.column) {
      resolved.action.column = resolve(resolved.action.column);
    }
    if (resolved.action.source_column) {
      resolved.action.source_column = resolve(resolved.action.source_column);
    }
    if (resolved.action.source_columns) {
      resolved.action.source_columns = resolved.action.source_columns.map(c => c ? resolve(c) : c);
    }
    if (resolved.action.formula_config?.source_column) {
      resolved.action.formula_config.source_column = resolve(resolved.action.formula_config.source_column);
    }
    if (resolved.action.formula_config?.operand_column) {
      resolved.action.formula_config.operand_column = resolve(resolved.action.formula_config.operand_column);
    }
    if (resolved.action.formula_config?.condition) {
      resolved.action.formula_config.condition = resolveCondition(resolved.action.formula_config.condition);
    }
  }

  return resolved;
}

function getTransformOptionsForType(type) {
  if (type === 'Date') return new Set(['format_date']);
  if (type === 'Number') return new Set(['round_number']);
  if (type === 'Text') return TEXT_TRANSFORMS;
  return new Set();
}

function validateConditionCompatibility(condition, columnTypeMap, errors, prefix = 'Condition') {
  if (!condition?.column) return;

  const lhsType = columnTypeMap[condition.column];
  if (!lhsType) {
    errors.push(`${prefix} column "${condition.column}" does not exist in the target table`);
    return;
  }

  const operatorConfig = OPERATOR_CONFIG[condition.operator];
  if (operatorConfig && !operatorConfig.types.includes(lhsType)) {
    errors.push(`${prefix} operator "${condition.operator}" is not valid for ${lhsType} column "${condition.column}"`);
  }

  const rhsColumns = [
    { source: condition.compare_source, column: condition.compare_column, label: 'comparison column' },
    { source: condition.compare_source2, column: condition.compare_column2, label: 'second comparison column' },
  ];

  rhsColumns.forEach(({ source, column, label }) => {
    if (source !== 'column' && !(source == null && column)) return;
    if (!columnTypeMap[column]) {
      errors.push(`${prefix} ${label} "${column}" does not exist in the target table`);
      return;
    }
    if (columnTypeMap[column] !== lhsType) {
      errors.push(`${prefix} ${label} "${column}" must match the type of "${condition.column}"`);
    }
  });
}

function validateActionCompatibility(rule, columnTypeMap, errors) {
  const action = rule.action || {};
  const targetColumn = rule.level === 'cell' ? rule.target_column : action.column;
  const targetType = targetColumn ? columnTypeMap[targetColumn] : null;

  if (['trim', 'remove_spaces', 'set_value', 'concatenate', 'transform_value'].includes(action.type)) {
    if (!targetColumn || !targetType) {
      errors.push(`Action "${action.type}" requires a valid target column`);
      return;
    }
  }

  if (['trim', 'remove_spaces', 'concatenate'].includes(action.type) && targetType !== 'Text') {
    errors.push(`Action "${action.type}" is only valid for Text target columns`);
  }

  if (action.type === 'concatenate') {
    (action.source_columns || []).forEach((column) => {
      if (!columnTypeMap[column]) {
        errors.push(`Concatenate source column "${column}" does not exist in the target table`);
      }
    });
  }

  if (action.type === 'transform_value') {
    const validTransforms = getTransformOptionsForType(targetType);
    if (!validTransforms.has(action.transformation)) {
      errors.push(`Transformation "${action.transformation}" is not valid for ${targetType} column "${targetColumn}"`);
    }
  }

  if (action.type !== 'set_value') {
    return;
  }

  if (action.value_type === 'column' && action.source_column) {
    const sourceType = columnTypeMap[action.source_column];
    if (!sourceType) {
      errors.push(`Source column "${action.source_column}" does not exist in the target table`);
    } else if (sourceType !== targetType) {
      errors.push(`Source column "${action.source_column}" must match the type of target column "${targetColumn}"`);
    }
  }

  if (action.value_source !== 'formula') {
    return;
  }

  const formulaType = action.formula_type;
  const formulaConfig = action.formula_config || {};
  if (!['prepend', 'append', 'arithmetic'].includes(formulaType)) {
    errors.push(`Unsupported formula type "${formulaType}"`);
    return;
  }

  if (['prepend', 'append'].includes(formulaType)) {
    if (targetType !== 'Text') {
      errors.push(`${formulaType} formulas are only valid for Text target columns`);
    }
    if (!formulaConfig.source_column || !columnTypeMap[formulaConfig.source_column]) {
      errors.push(`${formulaType} formulas require a valid source column`);
    }
    return;
  }

  if (formulaType === 'arithmetic') {
    if (!['Number', 'Date'].includes(targetType)) {
      errors.push('Arithmetic formulas are only valid for Number or Date target columns');
      return;
    }

    const sourceType = formulaConfig.source_column ? columnTypeMap[formulaConfig.source_column] : targetType;
    if (formulaConfig.source_column && !sourceType) {
      errors.push(`Arithmetic source column "${formulaConfig.source_column}" does not exist in the target table`);
      return;
    }

    const operation = formulaConfig.operation || (targetType === 'Date' ? 'add' : 'multiply');
    const validOperations = targetType === 'Date'
      ? new Set(['add', 'subtract'])
      : new Set(['add', 'subtract', 'multiply', 'divide']);
    if (!validOperations.has(operation)) {
      errors.push(`Arithmetic operation "${operation}" is not valid for ${targetType} targets`);
    }

    const expectedOperandType = targetType === 'Date'
      ? 'Number'
      : (operation === 'subtract' && sourceType === 'Date' ? 'Date' : 'Number');

    if (formulaConfig.operand_source === 'column' && formulaConfig.operand_column) {
      const operandType = columnTypeMap[formulaConfig.operand_column];
      if (!operandType) {
        errors.push(`Arithmetic operand column "${formulaConfig.operand_column}" does not exist in the target table`);
      } else if (operandType !== expectedOperandType) {
        errors.push(`Arithmetic operand column "${formulaConfig.operand_column}" must be ${expectedOperandType}`);
      }
    }
  }
}

function validateRuleCompatibility(rule, columnTypeMap) {
  const errors = [];
  (rule.conditions || []).forEach((condition, index) => {
    validateConditionCompatibility(condition, columnTypeMap, errors, `Condition ${index + 1}`);
  });
  validateActionCompatibility(rule, columnTypeMap, errors);
  return errors;
}

export default function ImportRuleLibraryDialog({ open, onClose, tableSchema, onImportRules, existingRules }) {
  const [activeStep, setActiveStep] = useState(0);
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLibraryId, setSelectedLibraryId] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [globalActionOverride, setGlobalActionOverride] = useState('');
  const [ruleSelections, setRuleSelections] = useState({});
  const [ruleActionOverrides, setRuleActionOverrides] = useState({});
  const [expandedRules, setExpandedRules] = useState({});

  // This dialog is mounted for one import. Its draft owns the column choices;
  // live table updates must not reinitialize a mapping the person is editing.
  const [tableColumns] = useState(() => getTableColumns(tableSchema));
  const [tableColumnTypeMap] = useState(() => getTableColumnTypeMap(tableSchema));

  // Libraries whose rules are already in the table are shown greyed out and cannot be
  // selected; an import never replaces rules or resets their enabled state (M235, L80).
  const existingLibraryIds = useMemo(() => {
    const ids = new Set();
    const rulesArr = Array.isArray(existingRules)
      ? existingRules
      : Object.values(existingRules || {}).filter(Boolean);
    rulesArr.forEach(r => {
      if (r?._library_id) ids.add(r._library_id);
    });
    return ids;
  }, [existingRules]);

  // Fetch libraries on open
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getDocs(collection(db, 'rule_libraries'))
      .then(snapshot => {
        const libs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setLibraries(libs);
      })
      .catch(err => {
        console.error('Failed to load rule libraries:', err);
      })
      .finally(() => setLoading(false));
  }, [open]);

  const selectedLibrary = useMemo(
    () => libraries.find(l => l.id === selectedLibraryId) || null,
    [libraries, selectedLibraryId]
  );

  // When a library is selected, auto-match columns and init rule selections
  useEffect(() => {
    if (!selectedLibrary) return;

    const autoMap = autoMatchColumns(selectedLibrary.canonical_columns || [], tableColumns);
    setColumnMapping(autoMap);

    const selections = {};
    (selectedLibrary.rules || []).forEach(rule => {
      selections[rule.id] = true;
    });
    setRuleSelections(selections);
    setRuleActionOverrides({});
    setGlobalActionOverride('');
    setExpandedRules({});
  }, [selectedLibrary, tableColumns]);

  // Determine which rules are importable (all referenced columns are mapped)
  const ruleImportability = useMemo(() => {
    if (!selectedLibrary) return {};
    const result = {};
    (selectedLibrary.rules || []).forEach(rule => {
      const refs = getReferencedColumns([rule]);
      const unmapped = [...refs].filter(col => !columnMapping[col]);
      const resolvedRule = resolveRule(rule, columnMapping);
      const typeErrors = unmapped.length === 0 ? validateRuleCompatibility(resolvedRule, tableColumnTypeMap) : [];
      const allRequired = (selectedLibrary.canonical_columns || [])
        .filter(c => c.required)
        .map(c => c.key);
      const missingRequired = unmapped.filter(col => allRequired.includes(col));
      result[rule.id] = {
        importable: unmapped.length === 0 && typeErrors.length === 0,
        unmappedColumns: unmapped,
        hasMissingRequired: missingRequired.length > 0,
        typeErrors,
      };
    });
    return result;
  }, [selectedLibrary, columnMapping, tableColumnTypeMap]);

  // Count importable + selected rules
  const importableCount = useMemo(() => {
    if (!selectedLibrary) return 0;
    return (selectedLibrary.rules || []).filter(r =>
      ruleSelections[r.id] && ruleImportability[r.id]?.importable
    ).length;
  }, [selectedLibrary, ruleSelections, ruleImportability]);

  // Validation: can we proceed from step 1 (column mapping)?
  const requiredColumnsMapped = useMemo(() => {
    if (!selectedLibrary) return false;
    const required = (selectedLibrary.canonical_columns || []).filter(c => c.required);
    return required.every(c => !!columnMapping[c.key]);
  }, [selectedLibrary, columnMapping]);

  const handleColumnMappingChange = useCallback((canonicalKey, tableColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [canonicalKey]: tableColumn || '',
    }));
  }, []);

  const handleGlobalActionChange = useCallback((actionType) => {
    setGlobalActionOverride(actionType);
    // Clear individual overrides when global is set
    if (actionType) {
      setRuleActionOverrides({});
    }
  }, []);

  const handleClose = useCallback(() => {
    setActiveStep(0);
    setSelectedLibraryId(null);
    setColumnMapping({});
    setGlobalActionOverride('');
    setRuleSelections({});
    setRuleActionOverrides({});
    setExpandedRules({});
    onClose();
  }, [onClose]);

  const handleImport = useCallback(() => {
    if (!selectedLibrary) return;

    const rulesToImport = (selectedLibrary.rules || []).filter(rule =>
      ruleSelections[rule.id] && ruleImportability[rule.id]?.importable
    );

    const concreteRules = rulesToImport.map(rule => {
      const resolved = resolveRule(rule, columnMapping);

      // Apply action override
      const actionOverride = ruleActionOverrides[rule.id] || globalActionOverride;
      if (actionOverride && resolved.action) {
        resolved.action.type = actionOverride;
      }

      // Generate new ID and add provenance metadata
      return {
        ...resolved,
        id: nanoid(),
        enabled: true,
        _library_id: selectedLibrary.id,
        _library_name: selectedLibrary.name,
        _library_version: selectedLibrary.version || '1.0',
        _library_rule_id: rule.id,
      };
    });

    onImportRules(concreteRules, selectedLibrary.id);
    handleClose();
  }, [selectedLibrary, ruleSelections, ruleImportability, columnMapping, ruleActionOverrides, globalActionOverride, onImportRules, handleClose]);

  const canProceedFromStep = (step) => {
    if (step === 0) return !!selectedLibraryId;
    if (step === 1) return requiredColumnsMapped;
    if (step === 2) return importableCount > 0;
    return false;
  };

  // --- Render Steps ---

  const renderLibrarySelection = () => (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : libraries.length === 0 ? (
        <Alert severity="info">
          No rule libraries are available yet. Libraries can be added by platform administrators.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {libraries.map(lib => {
            const alreadyImported = existingLibraryIds.has(lib.id);
            const isSelected = selectedLibraryId === lib.id;
            return (
              <Paper
                key={lib.id}
                onClick={alreadyImported ? undefined : () => setSelectedLibraryId(lib.id)}
                aria-disabled={alreadyImported}
                sx={{
                  p: 2.5,
                  cursor: alreadyImported ? 'not-allowed' : 'pointer',
                  opacity: alreadyImported ? 0.55 : 1,
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  backgroundColor: isSelected ? alpha('#4e85eb', 0.04) : 'background.paper',
                  transition: 'all 0.15s ease',
                  '&:hover': alreadyImported ? {} : {
                    borderColor: isSelected ? 'primary.main' : alpha('#4e85eb', 0.4),
                    backgroundColor: isSelected ? alpha('#4e85eb', 0.04) : alpha('#4e85eb', 0.02),
                  },
                }}
              >
                <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {lib.name}
                      </Typography>
                      <Chip label={`v${lib.version || '1.0'}`} size="small" variant="outlined" />
                      {alreadyImported && (
                        <Chip
                          label="Already imported"
                          size="small"
                          color="success"
                          variant="outlined"
                          icon={<CheckCircleOutlineIcon />}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {lib.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(lib.rules || []).length} rule{(lib.rules || []).length !== 1 ? 's' : ''} &middot;{' '}
                      {(lib.canonical_columns || []).length} column{(lib.canonical_columns || []).length !== 1 ? 's' : ''} to map
                    </Typography>
                    {alreadyImported && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Its rules are already in this table. Remove them from the rule list to import it again.
                      </Typography>
                    )}
                  </Box>
                  {isSelected && (
                    <CheckCircleOutlineIcon color="primary" sx={{ mt: 0.5 }} />
                  )}
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );

  const renderColumnMapping = () => {
    if (!selectedLibrary) return null;
    const canonicalColumns = selectedLibrary.canonical_columns || [];

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          Map each library column to the corresponding column in your table.
          Required columns must be mapped to proceed. Rules referencing unmapped optional columns will be skipped.
        </Alert>

        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {/* Header row */}
          <Box
            display="flex"
            sx={{
              px: 2, py: 1.5,
              backgroundColor: alpha('#4e85eb', 0.06),
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
              Library Column
            </Typography>
            <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
              Your Table Column
            </Typography>
          </Box>

          {canonicalColumns.map((col, idx) => (
            <Box
              key={col.key}
              display="flex"
              alignItems="center"
              sx={{
                px: 2, py: 1.5,
                borderBottom: idx < canonicalColumns.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Box flex={1} pr={2}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" fontWeight={500}>
                    {col.key}
                  </Typography>
                  {col.required && (
                    <Typography variant="caption" color="error.main" fontWeight={600}>*</Typography>
                  )}
                </Box>
                {col.description && (
                  <Typography variant="caption" color="text.secondary">
                    {col.description}
                  </Typography>
                )}
              </Box>
              <Box flex={1}>
                <FormControl fullWidth size="small">
                  <Select
                    value={columnMapping[col.key] || ''}
                    onChange={(e) => handleColumnMappingChange(col.key, e.target.value)}
                    displayEmpty
                    renderValue={(val) => val || <em style={{ color: '#999' }}>Not mapped</em>}
                  >
                    <MenuItem value="">
                      <em>Not mapped</em>
                    </MenuItem>
                    {tableColumns.map(tc => (
                      <MenuItem key={tc} value={tc}>{tc}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          ))}
        </Paper>

        {!requiredColumnsMapped && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2 }}>
            All required columns (marked with *) must be mapped before proceeding.
          </Alert>
        )}
      </Box>
    );
  };

  const renderActionConfiguration = () => {
    if (!selectedLibrary) return null;
    const rules = selectedLibrary.rules || [];

    return (
      <Box>
        {/* Global action override */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Default Action Override
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Optionally override the action for all imported rules. Leave as "Use library default" to keep each rule's
            original action.
          </Typography>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <Select
              value={globalActionOverride}
              onChange={(e) => handleGlobalActionChange(e.target.value)}
              displayEmpty
              renderValue={(val) => {
                if (!val) return <em>Use library default ({selectedLibrary.default_action_type || 'fail_row'})</em>;
                return ACTION_OPTIONS.find(o => o.value === val)?.label || val;
              }}
            >
              <MenuItem value="">
                <em>Use library default ({selectedLibrary.default_action_type || 'fail_row'})</em>
              </MenuItem>
              {ACTION_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label} &mdash; {opt.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        {/* Individual rules */}
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Rules to Import ({importableCount} of {rules.length})
        </Typography>

        <Stack spacing={1}>
          {rules.map(rule => {
            const importInfo = ruleImportability[rule.id] || {};
            const isSelected = ruleSelections[rule.id] && importInfo.importable;
            const isExpanded = expandedRules[rule.id];

            return (
              <Paper
                key={rule.id}
                variant="outlined"
                sx={{
                  overflow: 'hidden',
                  opacity: importInfo.importable ? 1 : 0.6,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ px: 2, py: 1.5 }}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={!importInfo.importable}
                    onChange={(e) => {
                      setRuleSelections(prev => ({
                        ...prev,
                        [rule.id]: e.target.checked,
                      }));
                    }}
                    size="small"
                  />
                  <Box flex={1} ml={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight={500}>
                        {rule.name}
                      </Typography>
                      <Chip
                        label={rule.level === 'cell' ? 'Cell' : 'Row'}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          backgroundColor: rule.level === 'cell'
                            ? alpha('#7c3aed', 0.1)
                            : alpha('#4e85eb', 0.1),
                          color: rule.level === 'cell' ? '#7c3aed' : '#4e85eb',
                        }}
                      />
                    </Box>
                    {!importInfo.importable && (
                      <Typography variant="caption" color="error.main">
                        {importInfo.unmappedColumns?.length > 0
                          ? `Cannot import: unmapped column${importInfo.unmappedColumns.length > 1 ? 's' : ''} ${importInfo.unmappedColumns.join(', ')}`
                          : `Cannot import: ${importInfo.typeErrors?.[0] || 'Rule is incompatible with this table'}`}
                      </Typography>
                    )}
                  </Box>

                  {importInfo.importable && (
                    <IconButton
                      size="small"
                      onClick={() => setExpandedRules(prev => ({
                        ...prev,
                        [rule.id]: !prev[rule.id],
                      }))}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  )}
                </Box>

                <Collapse in={isExpanded}>
                  <Divider />
                  <Box sx={{ px: 2, py: 1.5, backgroundColor: alpha('#000', 0.01) }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      Action override for this rule:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <Select
                        value={ruleActionOverrides[rule.id] || ''}
                        onChange={(e) => {
                          setRuleActionOverrides(prev => ({
                            ...prev,
                            [rule.id]: e.target.value,
                          }));
                        }}
                        displayEmpty
                        renderValue={(val) => {
                          if (!val) {
                            const effective = globalActionOverride || rule.action?.type || 'fail_row';
                            const label = ACTION_OPTIONS.find(o => o.value === effective)?.label || effective;
                            return <em>Use default ({label})</em>;
                          }
                          return ACTION_OPTIONS.find(o => o.value === val)?.label || val;
                        }}
                      >
                        <MenuItem value="">
                          <em>Use default</em>
                        </MenuItem>
                        {ACTION_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {rule.action?.message && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Error message: "{rule.action.message}"
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: 500 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <LibraryBooksIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Import Rule Library
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && renderLibrarySelection()}
        {activeStep === 1 && renderColumnMapping()}
        {activeStep === 2 && renderActionConfiguration()}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Box flex={1} />
        {activeStep > 0 && (
          <Button onClick={() => setActiveStep(prev => prev - 1)}>
            Back
          </Button>
        )}
        {activeStep < STEPS.length - 1 ? (
          <Button
            variant="contained"
            onClick={() => setActiveStep(prev => prev + 1)}
            disabled={!canProceedFromStep(activeStep)}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importableCount === 0}
          >
            {`Import ${importableCount} Rule${importableCount !== 1 ? 's' : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
